import { call, put } from 'redux-saga/effects';
import type { RequestSagaProps } from '~/types/actions.types';
import BaseException from '~/utils/fetch-wrapper/errors/BaseException';
import Logger from '~/utils/logger/logger';

type ActionPayload = RequestSagaProps['payload'];

const actions = {
  request: (type: string, payload: ActionPayload) => ({
    type,
    payload,
  }),
  success: <T>(type: string, payload: ActionPayload, response: T) => ({
    response,
    type,
    payload,
  }),
  failure: <E>(type: string, payload: ActionPayload, exception: E) => ({
    exception,
    type,
    payload,
    ...(exception instanceof BaseException && {
      error: exception.firstError,
      errors: exception.errors,
    }),
  }),
};

type Unpromisify<T> = T extends Promise<infer B> ? B : T;

type RequestSagaResult<Fn extends (...args) => Promise<any>> =
  | Unpromisify<ReturnType<Fn>>
  | BaseException;

type RequestSagaGeneratorType = <Fn extends (...args) => Promise<any>, At extends RequestSagaProps>(
  apiFunc: Fn,
  actionTypes: At,
  ...apiArgs: Parameters<Fn>
) => Generator<unknown, RequestSagaResult<Fn>, RequestSagaResult<Fn>>;

// This is a dummy generator that is just here for making sure that we can
//  get the correct return types in our sagas, and to make sure that our
//  tests does not have to run through the requestSaga2 every test
const callRequestSaga: RequestSagaGeneratorType = function* callRequestSaga(
  apiFunc,
  actionTypes,
  ...apiArgs
) {
  return yield call(requestSaga, apiFunc, actionTypes, ...apiArgs);
};

export function* requestSaga<Fn extends (...args) => Promise<any>, At extends RequestSagaProps>(
  apiFunc: Fn,
  actionTypes: At,
  ...apiArgs: Parameters<Fn>
): Generator<unknown, RequestSagaResult<Fn>, RequestSagaResult<Fn>> {
  try {
    if (actionTypes.request) {
      yield put(actions.request(actionTypes.request, actionTypes.payload));
    }

    const response = yield call(apiFunc, ...apiArgs);

    if (actionTypes.success) {
      yield put(actions.success(actionTypes.success, actionTypes.payload, response));
    }

    return response;
  } catch (exception) {
    Logger.error(exception);

    // @ts-expect-error -- unknown is not assignable here
    const thrown: RequestSagaResult<Fn> = exception;

    if (!actionTypes.failure) {
      return thrown;
    }

    yield put(actions.failure(actionTypes.failure, actionTypes.payload, thrown));
    return thrown;
  }
}

export default callRequestSaga;
